import { FaroTable } from "@components/common/faro-table/faro-table";
import { GroupBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { getTeamsColumns } from "@components/table/teams/teams-columns";
import { TeamsHeaders } from "@components/table/teams/teams-table-types";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { useDateTime } from "@hooks/use-date-time";
import { useMembers } from "@hooks/use-members";
import { useTeamUtils } from "@hooks/use-team-utils";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch } from "@store/store-helper";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { getMembersMapById } from "@utils/member-utils";
import { TableEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { HIDE_ON_HOVER_CLASS } from "@utils/ui-utils";
import { useCallback, useEffect, useMemo } from "react";

interface Props extends BaseCompanyIdProps {
  /** The teams that is shown in the table */
  teams: FaroCoreApiTypes.Team[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: TeamsHeaders[];

  /** List of all the bulk actions and callbacks */
  bulkActionButtons?: GroupBulkButtonComponents;
}

/** Listing all the teams of a company */
export function TeamsTable({
  companyId,
  teams,
  isLoading,
  requiredColumns,
  bulkActionButtons,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { formatDate } = useDateTime();
  const { openTeamDetails } = useTeamUtils();
  const { companyMembers } = useMembers();
  const companyMembersMap = getMembersMapById(companyMembers);

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.teams));

    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  const teamsColumns = useMemo(
    () =>
      requiredColumns.map(
        (requiredColumn) =>
          getTeamsColumns({
            companyId,
            formatDate,
            companyMembersMap,
          })[requiredColumn]
      ),
    [companyId, formatDate, companyMembersMap, requiredColumns]
  );

  // Callback fired when the sort option changes
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      if (!newModel || newModel.length === 0) {
        return;
      }

      const sortItem = newModel[0];
      if (!sortItem) {
        return;
      }

      // Track the sort event
      trackEvent({
        name: TableEvents.sortItems,
        props: {
          subject: "Team",
          sortBy: sortItem.field,
          sortOrder: sortItem.sort ?? "asc",
          viewMode: "list",
        },
      });
    },
    [trackEvent]
  );

  return (
    <FaroTable
      entities={teams}
      uniqueIdKey={"id"}
      isLoading={isLoading}
      columns={teamsColumns}
      bulkActionButtons={bulkActionButtons}
      rowHeight={70}
      sx={{
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },

        [`& .MuiDataGrid-row:hover .${HIDE_ON_HOVER_CLASS}`]: {
          display: "none",
        },
      }}
      onRowClick={(teamId) => openTeamDetails({ teamId })}
      onSortModelChange={handleSortModelChange}
      tableType={TableType.teams}
    />
  );
}
